import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { useAuth } from "../context/AuthContext";
import { api, apiErrorCode, apiErrorMessage } from "../lib/api";
import PhotoOrientationNudge from "../components/PhotoOrientationNudge";
import { usePhotoUploadQueue } from "../lib/usePhotoUploadQueue";

const PLANS = {
    free:    { name: "Free",    price: 0,  maxPhotos: 2 },
    plus:    { name: "Plus",    price: 10, maxPhotos: 3 },
    premium: { name: "Premium", price: 20, maxPhotos: 4 },
};

const SPACE_TYPES = [
    { id: "closet",       label: "Closet" },
    { id: "garage",       label: "Garage" },
    { id: "laundry_room", label: "Laundry room" },
    { id: "pantry",       label: "Pantry" },
    { id: "mudroom",      label: "Mudroom" },
    { id: "storage",      label: "Storage area" },
    { id: "kids_room",    label: "Kids' room" },
    { id: "home_office",  label: "Home office" },
    { id: "living_room",  label: "Living room" },
    { id: "bedroom",      label: "Bedroom" },
    { id: "other",        label: "Other" },
];

const STYLES = [
    { id: "modern",       label: "Modern" },
    { id: "minimal",      label: "Minimal" },
    { id: "scandinavian", label: "Scandinavian" },
    { id: "farmhouse",    label: "Farmhouse" },
    { id: "cozy_layered", label: "Cozy & layered" },
    { id: "natural",      label: "Natural / organic" },
];

const COLORS = [
    { id: "warm_neutrals", label: "Warm neutrals" },
    { id: "white",         label: "White & light" },
    { id: "sage",          label: "Sage green" },
    { id: "earth",         label: "Earth tones" },
    { id: "blue",          label: "Soft blues" },
    { id: "wood",          label: "Wood tones" },
];

export default function Intake() {
    const navigate = useNavigate();
    const [search] = useSearchParams();
    const planId = (search.get("plan") || "free").toLowerCase();
    const plan = PLANS[planId] || PLANS.free;
    const { member, signup, login, refresh } = useAuth();

    const [form, setForm] = useState({
        name: "",
        email: "",
        password: "",
        space_type: "",
        style: "",
        colors: "",
        problem: "",
    });
    const [photos, setPhotos] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [blocked, setBlocked] = useState(false);

    const uploadOne = async (file) => {
        const fd = new FormData();
        fd.append("file", file);
        const backend = process.env.REACT_APP_BACKEND_URL || "";
        const res = await fetch(`${backend}/api/uploads/photo`, {
            method: "POST",
            body: fd,
        });
        if (!res.ok) {
            let msg = `Upload failed (${res.status})`;
            try {
                const errJson = await res.json();
                if (errJson?.detail) msg = errJson.detail;
            } catch (_) {
                /* body wasn't JSON */
            }
            throw new Error(msg);
        }
        const data = await res.json();
        setPhotos((prev) => [...prev, { id: data.id, url: data.url }]);
    };

    const { uploading, pending, handleFiles, rotatePending, confirmPending } = usePhotoUploadQueue({
        uploadFile: async (file) => {
            try {
                await uploadOne(file);
            } catch (err) {
                toast.error(String(err?.message || "Photo upload failed. Please try again."));
            }
        },
        remainingSlots: plan.maxPhotos - photos.length,
    });

    useEffect(() => {
        refresh();
    }, [refresh]);

    useEffect(() => {
        if (!member) return;
        setForm((f) => ({
            ...f,
            name: f.name || member.name || "",
            email: f.email || member.email || "",
        }));
        if (plan.price === 0 && member.usage && member.usage.can_generate_free === false) {
            setBlocked(true);
        }
    }, [member, plan.price]);

    const canSubmit = useMemo(
        () =>
            form.name.trim() &&
            form.email.trim() &&
            form.space_type &&
            form.style &&
            form.colors &&
            form.problem.trim().length >= 5 &&
            (member || form.password.length >= 8),
        [form, member]
    );

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const onPickFiles = async (e) => {
        await handleFiles(e.target.files);
        e.target.value = "";
    };

    const removePhoto = (id) =>
        setPhotos((prev) => prev.filter((p) => p.id !== id));

    const submit = async () => {
        if (!canSubmit) return;
        if (pending) {
            toast.message("Confirm the ceiling is up on your photo — then continue. It only takes a tap.");
            return;
        }
        setSubmitting(true);
        try {
            // Map the streamlined intake to the backend's Lead schema
            const payload = {
                name: form.name.trim(),
                email: form.email.trim(),
                space_type: form.space_type,
                package_id: planId,
                style_prefs: [form.style],
                color_prefs: [form.colors],
                biggest_challenge: form.problem.trim(),
                goals: form.problem.trim(),
                photos: photos.map((p) => p.url),
                language: "en",
            };

            if (!member) {
                let nextMember;
                try {
                    nextMember = await signup({
                        name: form.name.trim(),
                        email: form.email.trim(),
                        password: form.password,
                    });
                } catch (authErr) {
                    if (apiErrorCode(authErr) === "EMAIL_IN_USE") {
                        nextMember = await login({
                            email: form.email.trim(),
                            password: form.password,
                        });
                    } else {
                        throw authErr;
                    }
                }
                if (plan.price === 0 && nextMember?.usage?.can_generate_free === false) {
                    setBlocked(true);
                    toast.error("You've already used your free Blueprint. Upgrade to organize another space.");
                    setSubmitting(false);
                    return;
                }
            }

            const { data: lead } = await api.post("/leads", payload);

            // Free tier: automation fires immediately on the backend
            if (plan.price === 0) {
                navigate(`/success?plan=free&lead=${lead.id}`);
                return;
            }

            // Paid tier: hand off to Stripe
            const origin = window.location.origin;
            const { data: checkout } = await api.post("/checkout/session", {
                package_id: planId,
                origin_url: origin,
                email: (member?.email || form.email).trim(),
                metadata: { lead_id: lead.id },
            });
            window.location.href = checkout.url;
        } catch (err) {
            if (apiErrorCode(err) === "FREE_TIER_LIMIT") {
                setBlocked(true);
            }
            toast.error(apiErrorMessage(err));
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-slate-50">
            <Header />
            <main className="container-app py-12 md:py-20">
                <div className="mx-auto max-w-2xl">
                    <div className="mb-10">
                        <span className="eyebrow">Step 1 of 1</span>
                        <h1 className="mt-4 font-display text-4xl font-light tracking-tight text-slate-900 sm:text-5xl">
                            Tell us about your space
                        </h1>
                        <p className="mt-3 text-slate-600">
                            Five quick questions and you&apos;re done. We&apos;ll build your
                            personalized FlowSpace Blueprint&trade; and email it to you
                            in about two minutes.
                        </p>
                        <div className="mt-6 inline-flex items-center gap-3 rounded-full border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm">
                            <span className="h-2 w-2 rounded-full bg-emerald-500" />
                            <span className="font-medium text-emerald-900">
                                {plan.name} plan
                            </span>
                            <span className="text-emerald-700">
                                {plan.price === 0 ? "Free" : `$${plan.price}`}
                            </span>
                        </div>
                    </div>

                    {blocked ? (
                        <div
                            className="rounded-3xl border border-amber-200 bg-white p-8 shadow-sm"
                            data-testid="intake-upgrade-wall"
                        >
                            <span className="eyebrow">Free plan used</span>
                            <h2 className="mt-4 font-display text-3xl font-light text-slate-900">
                                Your free Blueprint is already in your account
                            </h2>
                            <p className="mt-3 text-slate-600">
                                Free members get one organized space. Upgrade to Plus or Premium
                                to generate another plan — your existing spaces stay saved.
                            </p>
                            <div className="mt-8 flex flex-wrap gap-3">
                                <a href="/#packages" className="btn-primary" data-testid="intake-upgrade-cta">
                                    See Plus &amp; Premium
                                </a>
                                <Link to="/account" className="btn-ghost" data-testid="intake-view-spaces">
                                    View my spaces
                                </Link>
                            </div>
                        </div>
                    ) : (
                    <div className="space-y-6 rounded-3xl border border-slate-200 bg-white p-8 shadow-sm">
                        {/* 1. Space */}
                        <Field label="What kind of space are we working on?">
                            <select
                                value={form.space_type}
                                onChange={set("space_type")}
                                className="input"
                                data-testid="intake-space"
                            >
                                <option value="">Choose one…</option>
                                {SPACE_TYPES.map((s) => (
                                    <option key={s.id} value={s.id}>{s.label}</option>
                                ))}
                            </select>
                        </Field>

                        {/* 2. Style */}
                        <Field label="Which style feels most like you?">
                            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                                {STYLES.map((s) => (
                                    <Choice
                                        key={s.id}
                                        active={form.style === s.id}
                                        onClick={() => setForm((f) => ({ ...f, style: s.id }))}
                                        label={s.label}
                                        testId={`intake-style-${s.id}`}
                                    />
                                ))}
                            </div>
                        </Field>

                        {/* 3. Colors */}
                        <Field label="Pick a color palette you love">
                            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                                {COLORS.map((c) => (
                                    <Choice
                                        key={c.id}
                                        active={form.colors === c.id}
                                        onClick={() => setForm((f) => ({ ...f, colors: c.id }))}
                                        label={c.label}
                                        testId={`intake-color-${c.id}`}
                                    />
                                ))}
                            </div>
                        </Field>

                        {/* 4. Problem */}
                        <Field label="What's the main problem you want to solve?">
                            <textarea
                                rows={3}
                                value={form.problem}
                                onChange={set("problem")}
                                placeholder="e.g. Too much visual clutter, no place for my kids' art supplies, garage feels overwhelming…"
                                className="input"
                                data-testid="intake-problem"
                            />
                        </Field>

                        {/* 5. Photos (optional) */}
                        <Field
                            label={`Add photos of your space (optional — up to ${plan.maxPhotos})`}
                            hint="Helps the AI produce a more accurate render. You can skip this."
                        >
                            <div className="flex flex-wrap gap-3">
                                {photos.map((p) => (
                                    <div key={p.id} className="relative h-24 w-24 overflow-hidden rounded-xl border border-slate-200">
                                        <img
                                            src={`${process.env.REACT_APP_BACKEND_URL || ""}${p.url}`}
                                            alt="Uploaded"
                                            className="h-full w-full object-cover"
                                        />
                                        <button
                                            onClick={() => removePhoto(p.id)}
                                            className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-white/90 text-xs text-slate-700 shadow"
                                            aria-label="Remove"
                                        >
                                            ×
                                        </button>
                                    </div>
                                ))}
                                {photos.length < plan.maxPhotos && (
                                    <label className="flex h-24 w-24 cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-slate-300 text-xs text-slate-500 hover:border-emerald-400 hover:text-emerald-600">
                                        <input
                                            type="file"
                                            accept="image/*"
                                            multiple
                                            onChange={onPickFiles}
                                            className="hidden"
                                            disabled={uploading || Boolean(pending)}
                                            data-testid="intake-photo-input"
                                        />
                                        {uploading && !pending ? "…uploading" : "+ Add photo"}
                                    </label>
                                )}
                            </div>
                            {pending && (
                                <div className="mt-3">
                                    <PhotoOrientationNudge
                                        previewUrl={pending.previewUrl}
                                        reason={pending.inspection.reason}
                                        onRotate={rotatePending}
                                        onConfirm={confirmPending}
                                    />
                                </div>
                            )}
                        </Field>

                        {/* Contact + account */}
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                            <Field label="Your name">
                                <input
                                    value={form.name}
                                    onChange={set("name")}
                                    className="input"
                                    placeholder="Jane Doe"
                                    data-testid="intake-name"
                                />
                            </Field>
                            <Field label="Email (where we'll send your Blueprint)">
                                <input
                                    type="email"
                                    value={form.email}
                                    onChange={set("email")}
                                    className="input"
                                    placeholder="jane@example.com"
                                    disabled={Boolean(member)}
                                    data-testid="intake-email"
                                />
                            </Field>
                        </div>
                        {member ? (
                            <p className="text-sm text-slate-500" data-testid="intake-signed-in">
                                Signed in as {member.email}. This plan will be saved to your account.
                            </p>
                        ) : (
                            <div className="rounded-2xl border border-emerald-100 bg-emerald-50/70 p-4">
                                <Field
                                    label="Create a password to keep this plan"
                                    hint="A free member account lets you come back to this space. First Blueprint is free."
                                >
                                    <input
                                        type="password"
                                        value={form.password}
                                        onChange={set("password")}
                                        className="input"
                                        placeholder="At least 8 characters"
                                        autoComplete="new-password"
                                        data-testid="intake-password"
                                    />
                                </Field>
                                <p className="mt-3 text-xs text-slate-600">
                                    Already a member?{" "}
                                    <Link
                                        to={`/login?next=${encodeURIComponent(`/intake?plan=${planId}`)}`}
                                        className="font-medium text-emerald-700 hover:text-emerald-800"
                                        data-testid="intake-login-link"
                                    >
                                        Log in
                                    </Link>
                                    {" "}and we&apos;ll use that account instead.
                                </p>
                            </div>
                        )}

                        <div className="border-t border-slate-100 pt-6">
                            <button
                                onClick={submit}
                                disabled={!canSubmit || submitting}
                                className={`btn-primary w-full justify-center ${(!canSubmit || submitting) ? "opacity-50 cursor-not-allowed" : ""}`}
                                data-testid="intake-submit"
                            >
                                {submitting
                                    ? "Submitting…"
                                    : plan.price === 0
                                    ? "Get my free Blueprint"
                                    : `Continue to payment — $${plan.price}`}
                            </button>
                            <p className="mt-3 text-center text-xs text-slate-500">
                                {plan.price === 0
                                    ? "No payment. Your Blueprint arrives by email in about 2 minutes and stays in My spaces."
                                    : "Secure payment via Stripe. Blueprint delivered by email after payment."}
                            </p>
                        </div>
                    </div>
                    )}
                </div>
            </main>
            <Footer />
        </div>
    );
}

function Field({ label, hint, children }) {
    return (
        <div>
            <label className="mb-2 block text-sm font-medium text-slate-800">{label}</label>
            {children}
            {hint && <p className="mt-1.5 text-xs text-slate-500">{hint}</p>}
        </div>
    );
}

function Choice({ active, onClick, label, testId }) {
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={testId}
            className={`rounded-xl border px-4 py-3 text-sm font-medium transition-colors ${
                active
                    ? "border-emerald-500 bg-emerald-50 text-emerald-800"
                    : "border-slate-200 bg-white text-slate-700 hover:border-emerald-300 hover:text-emerald-700"
            }`}
        >
            {label}
        </button>
    );
}
